import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  rank?: number;
  variant?: 'default' | 'compact' | 'horizontal';
}

export default function ComicCard({ comic, rank, variant = 'default' }: ComicCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="group flex items-center gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors duration-200"
      >
        {rank && (
          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold flex-shrink-0">
            {rank}
          </span>
        )}
        <div className="w-12 h-16 rounded-md overflow-hidden flex-shrink-0 bg-background-200">
          <img
            src={comic.cover}
            alt={`${comic.title} 韩漫封面`}
            title={`${comic.title} - 韩国漫画免费在线观看`}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors duration-200">
            {comic.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-accent-500 font-medium">
              <i className="ri-star-fill text-[10px] mr-0.5"></i>
              {comic.rating}
            </span>
            <span className="text-xs text-foreground-400">{comic.category}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="group flex gap-4 p-3 rounded-xl bg-background-50 hover:bg-background-100 transition-all duration-300"
      >
        <div className="w-[100px] h-[140px] rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
          <img
            src={comic.cover}
            alt={`${comic.title} 韩漫封面`}
            title={`${comic.title} - 韩国漫画免费在线观看`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 flex flex-col justify-between py-1">
          <div>
            <h3 className="font-heading text-base font-bold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
              {comic.title}
            </h3>
            <p className="text-xs text-foreground-500 mt-1">{comic.author} · {comic.category}</p>
            <p className="text-sm text-foreground-600 mt-2 line-clamp-2">{comic.description}</p>
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                {comic.rating}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {(comic.views / 10000).toFixed(1)}万
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-heart-line"></i>
                {(comic.likes / 10000).toFixed(1)}万
              </span>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'}`}>
              {comic.status}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${comic.id}`}
      className="group block"
    >
      <div className="relative">
        <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
          <img
            src={comic.cover}
            alt={`${comic.title} 韩漫封面`}
            title={`${comic.title} - 韩国漫画免费在线观看`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="absolute top-2 left-2 flex gap-1.5 flex-wrap">
          {comic.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-white">
              {tag}
            </span>
          ))}
        </div>
        {rank && (
          <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-xs font-bold shadow-lg">
            {rank}
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${comic.status === '连载中' ? 'bg-primary-500/90 text-white' : 'bg-secondary-500/90 text-white'}`}>
            {comic.status}
          </span>
        </div>
      </div>
      <div className="mt-2.5">
        <h3 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
          {comic.title}
        </h3>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-foreground-500">{comic.author}</p>
          <span className="flex items-center text-xs text-accent-500 font-medium">
            <i className="ri-star-fill text-[10px] mr-0.5"></i>
            {comic.rating}
          </span>
        </div>
      </div>
    </Link>
  );
}